import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMenuLabels } from '@/features/menuLabels/hooks/useMenuLabels';
import { userDetailQueryOptions } from '@/features/users/api/users.api';
import { showApiErrorToast } from '@/utils/errors';
import {
  permissionCatalogueQueryOptions,
  permissionQueryKeys,
  rolePermissionsQueryOptions,
  setRolePermissions,
  setUserPermissions,
  userDirectPermissionsQueryOptions,
  userPermissionsQueryOptions,
  userRolesQueryOptions,
} from '../api/permissions.api';
import { roleQueryKeys, rolesQueryOptions } from '../api/roles.api';
import { buildPermissionMatrix, idsInUnit, type MatrixUnit } from '../types/permissionMatrix';
import type { PermissionItem } from '../types/permission.types';
import { isProtectedRole } from '../types/role.types';

const NO_PERMISSIONS: ReadonlySet<number> = new Set();

function matches(item: PermissionItem, label: string, query: string): boolean {
  return `${item.code} ${item.description} ${label}`.toLowerCase().includes(query);
}

export function usePermissionsSetup(userId?: number) {
  const isUserMode = userId !== undefined;
  const queryClient = useQueryClient();
  const { labelOf } = useMenuLabels();
  const [roleId, setRoleId] = useState<number | null>(null);
  const [editedIds, setEditedIds] = useState<ReadonlySet<number> | null>(null);
  const [searchText, setSearchText] = useState('');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const roles = useQuery({ ...rolesQueryOptions(), enabled: !isUserMode });
  const catalogue = useQuery(permissionCatalogueQueryOptions());
  const user = useQuery({
    ...userDetailQueryOptions(String(userId ?? 0)),
    enabled: isUserMode,
  });
  const roleHeld = useQuery(
    rolePermissionsQueryOptions(isUserMode ? undefined : (roleId ?? undefined)),
  );
  const userHeld = useQuery(userDirectPermissionsQueryOptions(userId));
  const effective = useQuery(userPermissionsQueryOptions(userId));
  const userRoles = useQuery(userRolesQueryOptions(userId));

  const held = isUserMode ? userHeld : roleHeld;
  const hasTarget = isUserMode || roleId !== null;

  const role = roles.data?.find((item) => item.id === roleId) ?? null;
  const isLocked = role !== null && isProtectedRole(role);

  const heldIds = useMemo(
    () => new Set((held.data ?? []).map((permission) => permission.id)),
    [held.data],
  );
  const selectedIds = editedIds ?? (held.data ? heldIds : NO_PERMISSIONS);

  const idByCode = useMemo(
    () => new Map((catalogue.data ?? []).map((permission) => [permission.code, permission.id])),
    [catalogue.data],
  );

  const roleNameByCode = useMemo(
    () => new Map((userRoles.data ?? []).map((role) => [role.code, role.name])),
    [userRoles.data],
  );

  const coveredBy = useMemo(() => {
    const byId = new Map<number, string[]>();
    for (const permission of effective.data ?? []) {
      if (permission.viaRoleCode === null) continue;
      const id = idByCode.get(permission.code);
      if (id === undefined || heldIds.has(id)) continue;
      const name = roleNameByCode.get(permission.viaRoleCode) ?? permission.viaRoleCode;
      const existing = byId.get(id);
      if (existing) existing.push(name);
      else byId.set(id, [name]);
    }
    return byId;
  }, [effective.data, heldIds, idByCode, roleNameByCode]);

  const query = searchText.trim().toLowerCase();
  const units: MatrixUnit[] = useMemo(() => {
    const all = buildPermissionMatrix(catalogue.data ?? [], labelOf);
    if (query === '') return all;

    return all
      .map((unit) => ({
        ...unit,
        rows: unit.rows.filter((row) =>
          [...row.byAction.values()].some((item) => matches(item, row.label, query)),
        ),
      }))
      .filter((unit) => unit.rows.length > 0);
  }, [catalogue.data, labelOf, query]);

  const addedCount = [...selectedIds].filter((id) => !heldIds.has(id)).length;
  const removedCount = [...heldIds].filter((id) => !selectedIds.has(id)).length;

  const mutation = useMutation({
    mutationFn: () =>
      isUserMode
        ? setUserPermissions(userId, [...selectedIds])
        : setRolePermissions(roleId!, [...selectedIds]),
    onSuccess: async () => {
      toast.success('Permissions saved');
      setIsConfirmOpen(false);
      setEditedIds(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: permissionQueryKeys.all() }),
        queryClient.invalidateQueries({ queryKey: roleQueryKeys.list() }),
      ]);
    },
    onError: (error) => showApiErrorToast(error, 'Could not save the permissions'),
  });

  function replaceSelection(change: (next: Set<number>) => void) {
    setEditedIds((current) => {
      const next = new Set(current ?? selectedIds);
      change(next);
      return next;
    });
  }

  return {
    isUserMode,
    userName: user.data?.fullName ?? '',
    userRoleNames: (userRoles.data ?? []).map((role) => role.name),
    roles: roles.data ?? [],
    roleId,
    role,
    isLocked,
    selectRole: (id: number | null) => {
      setRoleId(id);
      setEditedIds(null);
    },
    isLoading:
      roles.isLoading ||
      catalogue.isLoading ||
      user.isLoading ||
      effective.isLoading ||
      userRoles.isLoading ||
      held.isLoading,
    error: roles.error ?? catalogue.error ?? user.error ?? held.error,
    hasTarget,
    units,
    searchText,
    setSearchText,
    selectedIds,
    coveredBy,
    isCollapsed: (unit: string) => collapsed.has(unit),
    toggleUnit: (unit: string) =>
      setCollapsed((current) => {
        const next = new Set(current);
        if (next.has(unit)) next.delete(unit);
        else next.add(unit);
        return next;
      }),
    expandAll: () => setCollapsed(new Set()),
    collapseAll: () => setCollapsed(new Set(units.map((unit) => unit.unitKey))),
    toggle: (id: number) =>
      replaceSelection((next) => {
        if (next.has(id)) next.delete(id);
        else next.add(id);
      }),
    grantUnit: (unit: MatrixUnit, action: string | undefined, shouldGrant: boolean) =>
      replaceSelection((next) => {
        for (const id of idsInUnit(unit, action, coveredBy)) {
          if (shouldGrant) next.add(id);
          else next.delete(id);
        }
      }),
    isDirty: addedCount > 0 || removedCount > 0,
    addedCount,
    removedCount,
    isConfirmOpen,
    openConfirm: () => setIsConfirmOpen(true),
    closeConfirm: () => setIsConfirmOpen(false),
    save: () => mutation.mutate(),
    isSaving: mutation.isPending,
    reset: () => setEditedIds(null),
  };
}
